import React, { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

/**
 * CustomCursor
 *
 * Renders a copper dot + ring custom cursor driven by requestAnimationFrame.
 * The dot snaps to the mouse position instantly; the ring trails at a lerp
 * factor of 0.1, giving it a smooth inertial feel.
 *
 * Design decisions (locked by CONTEXT.md D-06..D-11):
 *   D-06: Copper color = oklch(0.62 0.13 45) — warm hue 45 mid-tone
 *   D-07: Dot 8px solid copper, snaps exactly (no lerp)
 *   D-08: Ring 32px copper at 50% opacity, lerp factor 0.1; zero React state on mousemove
 *   D-09: Hover morph — ring expands to 48px, dot fades to opacity 0
 *   D-10: Hidden on (pointer: coarse) and prefers-reduced-motion: reduce
 *   D-11: body.custom-cursor-active class drives cursor: none (wired in App.tsx)
 *
 * This component does NOT modify body.custom-cursor-active itself.
 * App.tsx owns that side effect via its location-aware useEffect.
 */

export const CustomCursor: React.FC = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  // Track (pointer: coarse) with lazy initializer + change listener
  const [isCoarse, setIsCoarse] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(pointer: coarse)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(pointer: coarse)');

    const handleChange = (event: MediaQueryListEvent): void => {
      setIsCoarse(event.matches);
    };

    mql.addEventListener('change', handleChange);
    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  // Refs for DOM mutation — zero React state on mousemove (D-08)
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const ringPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const hoveredRef = useRef<boolean>(false);
  const rafRef = useRef<number>(0);

  // Main effect: attach listeners + start rAF loop
  useEffect(() => {
    // Guard: early returns above prevent this effect from running when hidden,
    // but since hooks can't be called conditionally, we guard here too.
    if (prefersReducedMotion || isCoarse) return;

    const handleMouseMove = (e: MouseEvent): void => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };

    const handleMouseOver = (e: MouseEvent): void => {
      const target = e.target as Element | null;
      if (target && target.closest && target.closest('a, button, [data-cursor]')) {
        hoveredRef.current = true;
      }
    };

    const handleMouseOut = (e: MouseEvent): void => {
      const target = e.target as Element | null;
      const related = e.relatedTarget as Element | null;
      if (target && target.closest && target.closest('a, button, [data-cursor]')) {
        // Only clear hover if the pointer is not still over an interactive element
        if (!related || !related.closest || !related.closest('a, button, [data-cursor]')) {
          hoveredRef.current = false;
        }
      }
    };

    // WR-03: cancelled flag prevents the already-queued callback from scheduling
    // a new frame after cleanup runs (rAF race condition).
    let cancelled = false;

    const animate = (): void => {
      if (cancelled) return;

      // Dot snaps to cursor (D-07)
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mouseRef.current.x}px, ${mouseRef.current.y}px, 0) translate(-50%, -50%)`;
        dotRef.current.style.opacity = hoveredRef.current ? '0' : '1';
      }

      // Ring lerps toward cursor at factor 0.1 (D-08)
      ringPosRef.current.x += (mouseRef.current.x - ringPosRef.current.x) * 0.1;
      ringPosRef.current.y += (mouseRef.current.y - ringPosRef.current.y) * 0.1;

      if (ringRef.current) {
        // Hover morph: ring expands to 48px on interactive element hover (D-09)
        const size = hoveredRef.current ? 48 : 32;
        ringRef.current.style.width = `${size}px`;
        ringRef.current.style.height = `${size}px`;
        ringRef.current.style.transform = `translate3d(${ringPosRef.current.x}px, ${ringPosRef.current.y}px, 0) translate(-50%, -50%)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseover', handleMouseOver, { passive: true });
    document.addEventListener('mouseout', handleMouseOut, { passive: true });
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelled = true;
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      cancelAnimationFrame(rafRef.current);
    };
  }, [prefersReducedMotion, isCoarse]);

  // D-10: Render nothing on touch/coarse-pointer devices or when reduced motion is preferred
  if (prefersReducedMotion || isCoarse) return null;

  return (
    <>
      <style>{`
        .custom-cursor-dot {
          position: fixed;
          top: 0;
          left: 0;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: oklch(0.62 0.13 45);
          pointer-events: none;
          z-index: 9999;
          transition: opacity 0.2s ease;
          will-change: transform, opacity;
          contain: strict;
        }
        .custom-cursor-ring {
          position: fixed;
          top: 0;
          left: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid oklch(0.62 0.13 45 / 0.5);
          pointer-events: none;
          z-index: 9999;
          transition: width 0.2s ease, height 0.2s ease;
          will-change: transform, width, height;
          contain: strict;
        }
      `}</style>
      <div ref={dotRef} className="custom-cursor-dot" aria-hidden="true" />
      <div ref={ringRef} className="custom-cursor-ring" aria-hidden="true" />
    </>
  );
};

export default CustomCursor;
